import {
    centsToFormattedDollars,
    formattedDollarsToCents,
    roundCentsToWholeDollarCents,
} from '@fruitful/fruitils';
import {
    getMoneyMapperPeriodCentsAndPayCycle,
    IncomePayCycle,
    IncomeType,
    type MembershipIncome,
    type MembershipIncomeUpsertDto,
    moneyMapperStoredTakeHomeFromPeriod,
} from '@fruitful/member-profile';

export const INCOME_SAVED_TOAST_DEBOUNCE_MS = 2000;

/** Money Map main income table: Weekly → Bi-weekly → Semi-monthly → Monthly → Other. */
export const MAIN_PAY_CYCLE_OPTIONS: IncomePayCycle[] = [
    IncomePayCycle.WEEKLY,
    IncomePayCycle.BI_WEEKLY,
    IncomePayCycle.SEMI_MONTHLY,
    IncomePayCycle.MONTHLY,
    IncomePayCycle.OTHER,
];

/** Matches membership income / Add income flow income types. */
export const MAIN_INCOME_TYPE_OPTIONS: IncomeType[] = [
    IncomeType.FULL_TIME_SALARY,
    IncomeType.PART_TIME_WAGE,
    IncomeType.FREELANCE,
    IncomeType.CHILD_SUPPORT,
    IncomeType.ALIMONY,
    IncomeType.OTHER,
];

export interface IncomeDetailsFormState {
    belongsToUserId: string;
    description: string;
    type: IncomeType;
    periodAmountStr: string;
    payCycle: IncomePayCycle;
    notes: string;
}

export interface OwnerPickerItem {
    label: string;
    value: string;
}

export interface PayCyclePickerItem {
    label: string;
    value: IncomePayCycle;
}

export interface IncomeTypePickerItem {
    label: string;
    value: IncomeType;
}

export const centsToDollarInputDisplay = (cents: number): string => {
    return centsToFormattedDollars(
        roundCentsToWholeDollarCents(cents),
        false,
    ).replace('$', '$ ');
};

export const emptyForm = (
    ownerOptions: Record<string, string>,
): IncomeDetailsFormState => {
    const firstOwnerId = Object.values(ownerOptions)[0] ?? '';
    const defaultType =
        MAIN_INCOME_TYPE_OPTIONS[0] ?? IncomeType.FULL_TIME_SALARY;
    return {
        belongsToUserId: firstOwnerId,
        description: '',
        type: defaultType,
        periodAmountStr: '$ 0',
        payCycle: IncomePayCycle.BI_WEEKLY,
        notes: '',
    };
};

export const formFromIncome = (
    income: MembershipIncome,
): IncomeDetailsFormState => {
    const { periodCents, payCycle } =
        getMoneyMapperPeriodCentsAndPayCycle(income);
    return {
        belongsToUserId: income.belongsToUserId,
        description: income.description,
        type: income.type,
        periodAmountStr: centsToDollarInputDisplay(periodCents),
        payCycle,
        notes: income.additionalInformation ?? '',
    };
};

const normalizedRoundedPeriodCents = (
    periodAmountStr: string,
): number | null => {
    const raw = formattedDollarsToCents(periodAmountStr);
    if (Number.isNaN(raw)) {
        return null;
    }
    return roundCentsToWholeDollarCents(raw);
};

/** Main income table only: skip upsert when draft matches server-backed income. */
export const isTableIncomeFormUnchanged = (
    income: MembershipIncome,
    form: IncomeDetailsFormState,
): boolean => {
    const baseline = formFromIncome(income);
    if (form.belongsToUserId !== baseline.belongsToUserId) {
        return false;
    }
    if (form.payCycle !== baseline.payCycle) {
        return false;
    }
    if (form.type !== baseline.type) {
        return false;
    }
    if (form.description.trim() !== baseline.description.trim()) {
        return false;
    }
    if (form.notes.trim() !== baseline.notes.trim()) {
        return false;
    }
    const formCents = normalizedRoundedPeriodCents(form.periodAmountStr);
    const baseCents = normalizedRoundedPeriodCents(baseline.periodAmountStr);
    if (formCents === null || baseCents === null) {
        return form.periodAmountStr.trim() === baseline.periodAmountStr.trim();
    }
    return formCents === baseCents;
};

/** Default weekly hours when inferring part-time hourly from take-home (money mapper has no hours field). */
const DEFAULT_PART_TIME_WEEKLY_HOURS = 20;

/** Same factor as {@link getMonthlyCentsFromIncome} in member-profile (weekly → monthly). */
const EST_WEEKS_IN_MONTH = 4;

export const buildUpsertDto = ({
    existing,
    membershipId,
    form,
}: {
    existing: MembershipIncome | null;
    membershipId: string;
    form: IncomeDetailsFormState;
}): MembershipIncomeUpsertDto => {
    const periodCents = roundCentsToWholeDollarCents(
        formattedDollarsToCents(form.periodAmountStr),
    );
    const { monthlyTakeHomeCents, weeklyTakeHomeCents } =
        moneyMapperStoredTakeHomeFromPeriod(form.payCycle, periodCents);

    /**
     * Grove `AddIncomeFlow` / {@link PartTimeDetailsStep} always sends both take-home fields for
     * part-time. Money mapper often only has monthly (period → monthly); admin validation
     * expects `weeklyTakeHomeCents` for PART_TIME_WAGE — derive weekly from monthly when missing.
     */
    let resolvedMonthlyTakeHomeCents = monthlyTakeHomeCents;
    let resolvedWeeklyTakeHomeCents = weeklyTakeHomeCents;
    if (form.type === IncomeType.PART_TIME_WAGE) {
        if (
            (resolvedWeeklyTakeHomeCents == null ||
                resolvedWeeklyTakeHomeCents <= 0) &&
            resolvedMonthlyTakeHomeCents != null &&
            resolvedMonthlyTakeHomeCents > 0
        ) {
            resolvedWeeklyTakeHomeCents = roundCentsToWholeDollarCents(
                resolvedMonthlyTakeHomeCents / EST_WEEKS_IN_MONTH,
            );
        }
        if (
            (resolvedMonthlyTakeHomeCents == null ||
                resolvedMonthlyTakeHomeCents <= 0) &&
            resolvedWeeklyTakeHomeCents != null &&
            resolvedWeeklyTakeHomeCents > 0
        ) {
            resolvedMonthlyTakeHomeCents = roundCentsToWholeDollarCents(
                resolvedWeeklyTakeHomeCents * EST_WEEKS_IN_MONTH,
            );
        }
    }

    const {
        createdAt: _c,
        updatedAt: _u,
        ...existingWithoutMeta
    } = existing ?? {};

    /**
     * Admin API requires `annualSalaryCents` for FULL_TIME_SALARY (same as member
     * FullTimeDetailsStep). Money mapper only collects take-home in the table; derive a
     * reasonable gross placeholder from take-home so table-created full-time saves succeed.
     */
    const inferredAnnualSalaryCentsForFullTime =
        form.type === IncomeType.FULL_TIME_SALARY
            ? resolvedMonthlyTakeHomeCents != null &&
              resolvedMonthlyTakeHomeCents > 0
                ? roundCentsToWholeDollarCents(
                      resolvedMonthlyTakeHomeCents * 12,
                  )
                : resolvedWeeklyTakeHomeCents != null &&
                    resolvedWeeklyTakeHomeCents > 0
                  ? roundCentsToWholeDollarCents(
                        resolvedWeeklyTakeHomeCents * 52,
                    )
                  : undefined
            : undefined;

    const existingAnnual = existing?.annualSalaryCents;
    const resolvedAnnualSalaryCents =
        form.type === IncomeType.FULL_TIME_SALARY
            ? existingAnnual != null && existingAnnual > 0
                ? existingAnnual
                : inferredAnnualSalaryCentsForFullTime
            : null;

    const partTimeWeeklyHours =
        form.type === IncomeType.PART_TIME_WAGE
            ? existing?.weeklyHours != null && existing.weeklyHours > 0
                ? existing.weeklyHours
                : DEFAULT_PART_TIME_WEEKLY_HOURS
            : null;

    let partTimePayPerHourCents: number | null = null;
    if (
        form.type === IncomeType.PART_TIME_WAGE &&
        partTimeWeeklyHours != null
    ) {
        const existingHourly = existing?.payPerHourCents;
        if (existingHourly != null && existingHourly > 0) {
            partTimePayPerHourCents = existingHourly;
        } else {
            const safeHours = Math.max(1, partTimeWeeklyHours);
            let rawHourly: number | null = null;
            if (
                resolvedWeeklyTakeHomeCents != null &&
                resolvedWeeklyTakeHomeCents > 0
            ) {
                rawHourly = resolvedWeeklyTakeHomeCents / safeHours;
            } else if (
                resolvedMonthlyTakeHomeCents != null &&
                resolvedMonthlyTakeHomeCents > 0
            ) {
                const monthlyHours = (safeHours * 52) / 12;
                rawHourly = resolvedMonthlyTakeHomeCents / monthlyHours;
            }
            if (rawHourly != null) {
                const rounded = roundCentsToWholeDollarCents(rawHourly);
                partTimePayPerHourCents = rounded > 0 ? rounded : 100;
            }
        }
    }

    return {
        ...existingWithoutMeta,
        id: existing?.id,
        membershipId,
        type: form.type,
        belongsToUserId: form.belongsToUserId,
        description: form.description.trim(),
        payCycle: form.payCycle,
        monthlyTakeHomeCents: resolvedMonthlyTakeHomeCents,
        weeklyTakeHomeCents: resolvedWeeklyTakeHomeCents,
        annualSalaryCents: resolvedAnnualSalaryCents,
        payPerHourCents:
            form.type === IncomeType.PART_TIME_WAGE
                ? partTimePayPerHourCents
                : null,
        weeklyHours:
            form.type === IncomeType.PART_TIME_WAGE
                ? partTimeWeeklyHours
                : null,
        additionalCompensation:
            form.type === IncomeType.FULL_TIME_SALARY
                ? (existing?.additionalCompensation ?? undefined)
                : [],
        additionalInformation:
            form.notes.trim().length > 0
                ? form.notes.trim()
                : (existing?.additionalInformation ?? undefined),
    };
};
